<script lang="ts" setup>
const nuxtApp = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { parseMenuRoute, parseMenuTitle } = useNavbarParser()

const props = defineProps({
  menu: {
    type: Object as () =>
      | AwesomeLayoutPageNavbarMenuDropdownItem
      | AwesomeLayoutPageNavbarMenu,
    required: true,
  },
})
const isActive = computed(() => {
  if (!(props.menu as any)?.children) return false
  const childs = ((props.menu as any)?.children ||
    []) as AwesomeLayoutPageNavbarMenu[]
  for (const child of childs) {
    const to = parseMenuRoute(child.to)
    if (typeof to === 'string' && to === route.path) return true
    if (route.name?.toString().includes((to as any).name?.toString()))
      return true
  }
  return false
})

// 控制下拉菜单的显示状态
const isHovered = ref(false)
let hoverTimeout: NodeJS.Timeout | null = null

const handleMouseEnter = () => {
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
    hoverTimeout = null
  }
  lineOrigin.value = 'left' // 移入时从左边开始展开
  isHovered.value = true
}

const handleMouseLeave = () => {
  lineOrigin.value = 'right' // 移出时从右边开始收起（视觉上是从左向右消失）
  hoverTimeout = setTimeout(() => {
    isHovered.value = false
  }, 150)
}

// 横线动画的变换原点
const lineOrigin = ref('left')

// 下拉框和菜单容器的引用
const menuContainer = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

// 计算下拉框的位置（用于 fixed 定位）
const dropdownStyle = computed(() => {
  if (!menuContainer.value || !isHovered.value) {
    return {
      '-webkit-backdrop-filter': 'blur(5px)',
      'backdrop-filter': 'blur(5px)',
      'z-index': '60',
    }
  }
  const rect = menuContainer.value.getBoundingClientRect()
  return {
    top: `${rect.bottom + 30}px`,
    left: `${rect.left - 10}px`,
    '-webkit-backdrop-filter': 'blur(5px)',
    'backdrop-filter': 'blur(5px)',
    'z-index': '60',
  }
})
</script>

<template>
  <template v-if="menu.type !== 'dropdown'">
    <LayoutPageNavbarMenuItem :menu="menu" />
  </template>
  <template v-else-if="menu.children && menu.children.length > 0">
    <div
      ref="menuContainer"
      class="flex flex-col items-center relative"
      style="overflow: visible"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div class="relative">
        <div
          class="flex items-center transition-all duration-300 text-gray-900 cursor-pointer"
        >
          <!-- 暗黑模式已禁用: dark:text-gray-100 -->
          <span :class="[isActive ? 'font-bold' : '']">{{
            parseMenuTitle(menu.title)
          }}</span>
          <Icon
            name="carbon:chevron-down"
            class="ml-1 transition-transform duration-200"
            :class="[isHovered ? 'transform rotate-180' : '']"
          />
        </div>
        <div
          class="w-[58px] h-[4px] rounded-full absolute -bottom-[6px] transition-transform duration-300 ease-out"
          :class="[isActive || isHovered ? 'scale-x-100' : 'scale-x-0']"
          :style="`background: linear-gradient(135deg, #37beef 0%, #47b897 100%); left: calc(50% - 29px); transform-origin: ${lineOrigin}`"
        ></div>
      </div>
      <Teleport to="body">
        <Transition
          enter-active-class="transition-opacity duration-200 ease-out"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-150 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="isHovered"
            ref="dropdownRef"
            class="dropdown-menu text-[14px] fixed px-2 py-2 rounded-lg w-max pointer-events-auto bg-[#F1F1F1B2]"
            :style="dropdownStyle"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
          >
            <div class="grid grid-cols-1">
              <template v-for="(child, j) in menu.children" :key="j">
                <LayoutPageNavbarMenuItem :menu="child" is-dropdown />
              </template>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </template>
</template>

<style scoped>
.dropdown-menu {
  filter: saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.5);
}
</style>
